(ns ductile.ui.load-builder-v2.panels
  (:require [clojure.string :as str]
            [ductile.ui.components :as ui]
            [ductile.ui.components.columns :as columns]
            [ductile.ui.components.filters :as filters]
            [ductile.ui.components.popover :as popover]
            [ductile.ui.components.sortable :as sortable]
            [ductile.ui.components.table :as table]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.fields :as fields]
            [ductile.ui.load-builder-v2.move-menu :as move-menu]
            [ductile.ui.load-builder-v2.selectors :as lb2sel]
            [ductile.ui.load-builder.overview-map :as overview-map]
            [ductile.ui.locale :refer [tr]]
            [ductile.ui.paneling :as paneling]
            [ductile.ui.transport :as transport]
            [nextjournal.clerk.render.hooks :as hooks]))

(defn waypoint-label [{:address/keys [postcode city]}]
  (str (or postcode "?") " " (or city (tr "Unknown"))))

(defn canonical-waypoints [vehicle-ids vehicle-id->transport waypoint-key-fn]
  (->> vehicle-ids
       (keep vehicle-id->transport)
       (keep :transport/destination)
       (reduce (fn [{:keys [seen acc]} wp]
                 (let [k (waypoint-key-fn wp)]
                   (if (contains? seen k)
                     {:seen seen :acc acc}
                     {:seen (conj seen k)
                      :acc (conj acc wp)})))
               {:seen #{} :acc []})
       :acc
       vec))

(defn header-link-button [{:keys [on-click label testid]}]
  [:button.bg-transparent.border-0.p-0.m-0
   {:data-testid testid
    :class "text-[12px] text-indigo-700 hover:text-indigo-800 underline underline-offset-2 decoration-slate-300 hover:decoration-indigo-500"
    :on-click on-click}
   label])

(defn transport-more-dropdown [{:keys [focused? on-print-load-list on-focus-map on-hide-map]}]
  (let [items (remove nil?
                      [(when on-print-load-list
                         {:key :print-load-list
                          :data-testid "transport-more-print-load-list"
                          :label (tr "Print load list")
                          :on-select on-print-load-list})
                       {:key :show-hide-map
                        :data-testid (if focused?
                                       "transport-more-hide-map"
                                       "transport-more-show-map")
                        :label (if focused?
                                 (tr "Hide from map")
                                 (tr "Show on map"))
                        :on-select (if focused? on-hide-map on-focus-map)}])]
    [popover/menu-select
     {:full-width? false
      :class "flex"
      :button-class "bg-transparent border-0 p-0 pr-0 m-0 inline-flex items-center leading-none align-middle text-[12px] text-indigo-700 hover:text-indigo-800 underline underline-offset-2 decoration-slate-300 hover:decoration-indigo-500"
      :button-content [:span.inline-flex.items-center.gap-1.cursor-pointer.leading-none.align-middle
                       (tr "More")
                       [:span.inline-flex.items-center.relative {:style {:top "1px"}}
                        [ui/icon {:size 10} :chevron-down]]]
      :data-testid "transport-more-actions"
      :items items}]))

(defn inline-separated [items]
  (into
   [:<>]
   (interpose
    [:span.text-slate-400 "•"]
    (remove nil? items))))

(defn- header-interactive-target? [^js event]
  (boolean
   (some-> (.-target event)
           (.closest "button,a,input,textarea,select,label,[role='button']"))))

(defn format-last-update-time [last-update-ms]
  (when (number? last-update-ms)
    (let [date (js/Date. last-update-ms)]
      (.toLocaleTimeString date
                           js/undefined
                           #js {:hour "2-digit"
                                :minute "2-digit"
                                :second "2-digit"
                                :hour12 false}))))

(defn last-update-indicator [{:keys [last-update-ms auto-refresh-seconds]}]
  (let [refresh-ms (max 1000 (* 1000 (or auto-refresh-seconds 5)))
        !displayed-update-ms (hooks/use-state (or last-update-ms (.now js/Date)))]
    (hooks/use-effect
     (fn []
       (when (number? last-update-ms)
         (reset! !displayed-update-ms last-update-ms)))
     [last-update-ms])
    (hooks/use-effect
     (fn []
       (let [timer (js/setInterval
                    #(reset! !displayed-update-ms (.now js/Date))
                    refresh-ms)]
         #(js/clearInterval timer)))
     [refresh-ms])
    (when-let [last-update-text (format-last-update-time @!displayed-update-ms)]
      [:span.text-slate-500
       {:class "text-[12px] whitespace-nowrap"
        :title (tr "Load builder auto-refreshes every %1 seconds"
                   (or auto-refresh-seconds "?"))}
       (tr "Last Update: %1" last-update-text)])))

(defn transport-status-badge [status]
  (let [[bg-class text-class] (case status
                                :transport.status/in-planning ["bg-slate-100" "text-slate-700"]
                                :transport.status/awaiting-compound ["bg-sky-100" "text-sky-700"]
                                :transport.status/unloading-not-started ["bg-sky-100" "text-sky-700"]
                                :transport.status/partially-unloaded ["bg-orange-100" "text-orange-700"]
                                :transport.status/partially-prepared ["bg-orange-100" "text-orange-700"]
                                :transport.status/fully-prepared ["bg-green-100" "text-green-700"]
                                :transport.status/fully-unloaded ["bg-green-100" "text-green-700"]
                                :transport.status/ready-for-pickup ["bg-blue-100" "text-blue-700"]
                                :transport.status/exited ["bg-fuchsia-100" "text-fuchsia-700"]
                                :transport.status/deleted ["bg-slate-200" "text-slate-800"]
                                ["bg-slate-100" "text-slate-700"])]
    [:span.inline-flex.rounded.px-1.py-0.font-bold
     {:class (str "text-[11px] " bg-class " " text-class)}
     (tr (or status "Unknown"))]))

(defn waypoint-order-modal [{:keys [open?
                                    transport-id
                                    modal-load-title
                                    modal-carrier-title
                                    draft-waypoints
                                    draft-optimized-route?
                                    optimizing?
                                    error
                                    on-close
                                    on-save
                                    on-set-draft-waypoints
                                    on-set-optimized-route
                                    on-optimize-waypoints
                                    waypoint-key-fn]}]
  (when open?
    [ui/modal {:on-close on-close
               :close-attrs {:class "top-2 right-2 p-1"}
               :attrs {:class "w-[640px] max-w-[94vw]"}}
     [:div.p-4.flex.flex-col.gap-3
      [:div
       [:div.font-bold.text-slate-800
        (tr "Edit route order for")
        (when (seq modal-load-title)
          (str " " modal-load-title))
        (when (seq modal-carrier-title)
          [:span.text-slate-500.font-normal
           (str " " modal-carrier-title)])]
       [:div.text-slate-600 {:class "text-[12px]"}
        (tr "Drag waypoints to set a custom order, or enable optimized route.")]]
      [:div.rounded.border.border-slate-300.bg-slate-50.p-3.flex.items-center.justify-between
       [:label.inline-flex.flex-col.gap-1.select-none.cursor-pointer
        [:div.flex.items-center.gap-3
         [:input
          {:type "checkbox"
           :checked (boolean draft-optimized-route?)
           :disabled (boolean optimizing?)
           :on-change (fn [event]
                        (let [checked? (.. event -target -checked)]
                          (if checked?
                            (do
                              (on-set-optimized-route true)
                              (on-optimize-waypoints))
                            (on-set-optimized-route false))))}]
         [:div {:class "flex flex-col"}
          [:div.font-medium {:class "text-[12px]"} (tr "Optimized Route")]
          [:span.text-slate-500 {:class "text-[11px]"}
           (tr "Manual sorting is reverted when Optimized Route is enabled.")]]]]
       (when optimizing?
         [:div.flex.items-center.gap-2.text-indigo-700 {:class "text-[12px]"}
          [ui/spinner {:size 12}]
          [:span (str (tr "Optimizing") "...")]])]
      (when (seq error)
        [:div.rounded.border.border-red-200.bg-red-50.px-2.py-1.text-red-700
         {:class "text-[12px]"}
         error])
      [:div.bg-white.overflow-auto.w-full
       {:class "max-h-[52vh]"}
       [sortable/sortable
        {:items draft-waypoints
         :key-fn waypoint-key-fn
         :axis :y
         :debug? false
         :disabled? (or optimizing? (< (count draft-waypoints) 2))
         :handle-selector ".waypoint-sort-handle"
         :on-drag-start #(on-set-optimized-route false)
         :container-class "flex flex-col w-full"
         :item-class "w-full rounded border border-slate-200 bg-white mb-2 last:mb-0"
         :on-reorder (fn [{:keys [items]}]
                       (on-set-draft-waypoints items)
                       (on-set-optimized-route false))
         :render-item
         (fn [waypoint idx]
           [:div.px-2.py-2.flex.items-center.gap-3
            [:div.w-7.h-7.rounded-full.border.border-slate-300.bg-slate-50.text-slate-600.inline-flex.items-center.justify-center.tabular-nums.flex-shrink-0
             {:class "text-[11px] font-semibold"}
             (inc idx)]
            [:div.flex-1.min-w-0.pr-1
             [:div.font-medium.truncate {:class "text-[12px]"}
              (str (or (:address/postcode waypoint) "?")
                   " "
                   (or (:address/city waypoint) (tr "Unknown")))]
             (when (seq (:address/name waypoint))
               [:div.text-slate-500.truncate {:class "text-[11px]"}
                (:address/name waypoint)])]
            [:div.bg-white.rounded-sm.border.flex.items-center.justify-center.flex-shrink-0
             {:class "w-[24px] h-[24px] waypoint-sort-handle cursor-move text-slate-500 hover:border-indigo-300 hover:text-indigo-700"}
             [ui/icon :drag-handle-horizontal]]])}]]
      [:div.flex.justify-end.gap-2.pt-1
       [ui/button {:variant :white
                   :size 2
                   :on-click on-close}
        (tr "Cancel")]
       [ui/button {:variant :primary
                   :size 2
                   :disabled? (boolean optimizing?)
                   :on-click on-save}
        (tr "Save route")]]]]))

(def no-view-option :load-builder-v2/no-view)
(def save-view-option :load-builder-v2/save-view)
(def rename-view-option :load-builder-v2/rename-view)
(def duplicate-view-option :load-builder-v2/duplicate-view)
(def delete-view-option :load-builder-v2/delete-view)

(defn views-select-dropdown
  [{:keys [views selected-view-id modified?
           on-select on-save on-rename on-duplicate on-delete
           on-save-changes on-save-as-new on-discard-changes
           modified-lines
           text-class]
    :or {text-class "text-xs"}}]
  (let [selected-view (lb2sel/current-view views selected-view-id)
        selected-label (or (:name selected-view) (tr "No view selected"))
        items (concat
               [{:key no-view-option
                 :label (tr "No view selected")
                 :on-select #(on-select nil)
                 :data-testid "views-select-dropdown-no-view"}]
               (map (fn [{:keys [id name]}]
                      {:key id
                       :label name
                       :on-select #(on-select id)
                       :data-testid (str "views-select-dropdown-item-" id)})
                    views)
               [{:separator? true}
                {:key save-view-option
                 :label (str (tr "Save this view") "…")
                 :on-select on-save
                 :data-testid "views-select-dropdown-save-view"}]
               (when selected-view
                 [{:key rename-view-option
                   :label (str (tr "Rename \"%1\"" (:name selected-view)) "…")
                   :on-select on-rename
                   :data-testid "views-select-dropdown-rename-view"}
                  {:key duplicate-view-option
                   :label (tr "Duplicate \"%1\"" (:name selected-view))
                   :on-select on-duplicate
                   :data-testid "views-select-dropdown-duplicate-view"}
                  {:key delete-view-option
                   :label (str (tr "Delete \"%1\"" (:name selected-view)) "…")
                   :on-select on-delete
                   :data-testid "views-select-dropdown-delete-view"}]))]
    [:div.flex.items-center
     [popover/menu-select
      {:class (str "ml-2 flex items-center gap-1 cursor-pointer text-gray-600 hover:text-gray-700 active:text-gray-800 relative " text-class)
       :button-class "pl-4 pr-1"
       :data-testid "views-select-dropdown"
       :icon :binoculars
       :label selected-label
       :items items}]

     (when (and selected-view modified?)
       [popover/menu-select
        {:full-width? false
         :menu-class "max-w-[350px]"
         :button-class "bg-transparent border-0 p-0 m-0"
         :button-content [ui/badge {:variant :yellow
                                    :size 1
                                    :class "gap-1 cursor-pointer"}
                          (tr "Modified")
                          [ui/icon {:size 10} :chevron-down]]
         :data-testid "views-modified-badge"
         :items (concat
                 (when (seq modified-lines)
                   [{:content
                     [:div.px-2.pt-1.pb-3
                      {:class "text-slate-500"}
                      (into [:div.flex.flex-col.gap-0.5]
                            (for [line modified-lines]
                              ^{:key line}
                              [:div line]))]}])
                 [{:key :save-changes
                   :data-testid "view-save-changes"
                   :label (tr "Save changes")
                   :on-select on-save-changes}
                  {:key :save-as-new
                   :data-testid "view-save-as-new"
                   :label (tr "Save as new")
                   :on-select on-save-as-new}
                  {:key :discard-changes
                   :data-testid "view-discard-changes"
                   :label (tr "Discard changes")
                   :on-select on-discard-changes}])}])]))

(defn transport-card [{:keys [transport-id transport vehicle-id->transport carrier-label draft? waypoints vehicle-ids focused?
                              draft-index table-opts-fn
                              status truck-or-carrier-code license-plate collapsed? on-toggle-collapsed
                              selected-vehicle-ids on-drag-select on-drag-select-preview
                              on-select-row
                              on-focus-map on-hide-map on-edit on-save-draft on-dismiss-draft on-rename-draft
                              on-print-load-list
                              on-open-transport-menu
                              on-save-waypoint-settings optimize-waypoints! waypoint-settings waypoint-key-fn
                              load-weight-label
                              on-right-click-vehicle col-settings update-token]}]
  (let [waypoints-text (if (seq waypoints)
                         (str/join " -> " (map waypoint-label waypoints))
                         nil)
        persisted-waypoint-order (or (get-in transport [:ductile/data :destination/ductile-ids])
                                     (get-in transport [:transport/transport :ductile/data :destination/ductile-ids]))
        default-optimized-route? (if (contains? waypoint-settings :optimized-route?)
                                   (not= false (:optimized-route? waypoint-settings))
                                   true)
        !editing-draft-name? (hooks/use-state false)
        !draft-name-input (hooks/use-state (or (:draft/name transport) ""))
        !editing-waypoints? (hooks/use-state false)
        !draft-waypoints (hooks/use-state (vec waypoints))
        !draft-optimized-route? (hooks/use-state default-optimized-route?)
        !optimizing-waypoints? (hooks/use-state false)
        !waypoint-error (hooks/use-state nil)
        !flash-outline? (hooks/use-state false)
        !card-ref (hooks/use-ref nil)
        !compact-layout? (hooks/use-state false)
        compact-layout-threshold 780
        draft-title (str (tr "Draft transport") " " (or draft-index 1))
        title (if draft?
                (or (:draft/name transport) draft-title)
                (:load/id transport))
        modal-load-title title
        modal-carrier-title carrier-label
        vehicles (vec (keep vehicle-id->transport vehicle-ids))
        optimization-source-waypoints (canonical-waypoints vehicle-ids vehicle-id->transport waypoint-key-fn)
        open-waypoint-editor! (fn []
                                (reset! !draft-waypoints (vec waypoints))
                                (reset! !draft-optimized-route? default-optimized-route?)
                                (reset! !optimizing-waypoints? false)
                                (reset! !waypoint-error nil)
                                (reset! !editing-waypoints? true))
        close-waypoint-editor! (fn []
                                 (reset! !editing-waypoints? false)
                                 (reset! !optimizing-waypoints? false)
                                 (reset! !waypoint-error nil))
        optimize-draft-waypoints! (fn []
                                    (when optimize-waypoints!
                                      (reset! !optimizing-waypoints? true)
                                      (reset! !waypoint-error nil)
                                      (-> (optimize-waypoints! transport-id optimization-source-waypoints)
                                          (.then (fn [optimized-waypoints]
                                                   (reset! !draft-waypoints (vec optimized-waypoints))))
                                          (.catch (fn [_]
                                                    (reset! !waypoint-error (tr "Failed to optimize route. Keeping current order."))
                                                    (reset! !draft-optimized-route? false)))
                                          (.finally #(reset! !optimizing-waypoints? false)))))
        save-waypoint-editor! (fn []
                                (when on-save-waypoint-settings
                                  (on-save-waypoint-settings transport-id
                                                             {:optimized-route? (boolean @!draft-optimized-route?)
                                                              :waypoint-order (mapv waypoint-key-fn @!draft-waypoints)}))
                                (close-waypoint-editor!))
        table-opts (table-opts-fn
                    {:col-settings col-settings
                     :selected-rows (vec selected-vehicle-ids)
                     :drag-select? true
                     :on-drag-select-preview on-drag-select-preview
                     :on-drag-select-commit on-drag-select
                     :on-drag-select on-drag-select
                     :row-action (fn [vehicle event]
                                   (on-select-row event (:ductile/id vehicle) (mapv :ductile/id vehicles)))
                     :row-on-context-menu (fn [vehicle event]
                                            (.preventDefault event)
                                            (.stopPropagation event)
                                            (on-right-click-vehicle event (:ductile/id vehicle) transport-id))})
        collapse-button
        [:button.bg-transparent.border-0.p-0.m-0.inline-flex.items-center.justify-center.text-slate-500.hover:text-slate-700
         {:data-testid "transport-collapse-toggle"
          :on-click #(on-toggle-collapsed transport-id)
          :aria-label (if collapsed? (tr "Expand transport") (tr "Collapse transport"))}
         [:span.inline-flex.transition-transform.duration-150.relative
          {:style {:top "1px"}
           :class (if collapsed? "" "rotate-[90deg]")}
          [ui/icon {:size 12} :chevron-right]]]
        title-node
        (if (and draft? @!editing-draft-name?)
          [:div.flex.items-center.gap-2.flex-wrap
           [:input.border.border-slate-300.rounded.px-2.py-0.5
            {:class "text-[12px]"
             :data-testid "draft-rename-input"
             :value @!draft-name-input
             :auto-focus true
             :placeholder draft-title
             :on-change #(reset! !draft-name-input (.. % -target -value))
             :on-key-down (fn [^js e]
                            (case (.-key e)
                              "Enter" (do (on-rename-draft transport-id (str/trim @!draft-name-input))
                                          (reset! !editing-draft-name? false))
                              "Escape" (do (reset! !draft-name-input (or (:draft/name transport) ""))
                                           (reset! !editing-draft-name? false))
                              nil))}]
           [header-link-button
            {:on-click (fn []
                         (on-rename-draft transport-id (str/trim @!draft-name-input))
                         (reset! !editing-draft-name? false))
             :testid "draft-rename-save"
             :label (tr "Save")}]
           [header-link-button
            {:on-click (fn []
                         (reset! !draft-name-input (or (:draft/name transport) ""))
                         (reset! !editing-draft-name? false))
             :testid "draft-rename-cancel"
             :label (tr "Cancel")}]]
          (if draft?
            [:button.bg-transparent.border-0.p-0.m-0.group.inline-flex.items-center.gap-1.leading-none.align-middle
             {:data-testid "draft-rename-start"
              :on-click #(do
                           (reset! !draft-name-input (or (:draft/name transport) ""))
                           (reset! !editing-draft-name? true))}
             [:span.font-bold {:data-testid "draft-title"} title]
             [:span.inline-flex.items-center.opacity-0.group-hover:opacity-70.text-slate-500
              [ui/icon {:size 12} :pencil-1]]]
            [:span.font-bold title]))
        more-dropdown
        [:div.flex.items-center.ml-1
         [transport-more-dropdown
          {:focused? focused?
           :on-print-load-list on-print-load-list
           :on-focus-map on-focus-map
           :on-hide-map on-hide-map}]]
        route-edit-button
        (when (seq waypoints)
          [:div.flex.items-center.shrink-0
           [header-link-button
            {:on-click open-waypoint-editor!
             :testid "transport-edit-waypoints"
             :label (tr "Edit route")}]])]
    (hooks/use-effect
     (fn []
       (when (some? update-token)
         (reset! !flash-outline? true)
         (let [timeout-id (js/setTimeout #(reset! !flash-outline? false) 380)]
           #(js/clearTimeout timeout-id))))
     [update-token])
    (hooks/use-effect
     (fn []
       (when-let [el @!card-ref]
         (let [update-layout! #(reset! !compact-layout?
                                       (< (.-width (.getBoundingClientRect el))
                                          compact-layout-threshold))
               ^js resize-observer (js/ResizeObserver. update-layout!)]
           (update-layout!)
           (.observe resize-observer el)
           #(do (.disconnect resize-observer))))
       js/undefined)
     [@!card-ref compact-layout-threshold])
    [:div.rounded.border.border-slate-300.bg-white.shadow-sm.overflow-hidden
     {:data-testid "transport-card"
      :data-transport-id (str transport-id)
      :data-draft (str (boolean draft?))
      :ref !card-ref
      :on-context-menu (fn [^js event]
                         (when on-open-transport-menu
                           (.preventDefault event)
                           (on-open-transport-menu event
                                                   {:transport-id transport-id
                                                    :draft? draft?
                                                    :focused? focused?
                                                    :collapsed? collapsed?
                                                    :show-edit-route? (boolean route-edit-button)
                                                    :on-edit-route open-waypoint-editor!
                                                    :on-toggle-collapsed on-toggle-collapsed
                                                    :show-print-load-list? (boolean on-print-load-list)})))
      :class "relative hover:z-[1] hover:outline hover:outline-1 hover:outline-indigo-500"}
     [:div.pointer-events-none.absolute.inset-0.rounded.z-20
      {:style {:box-shadow (if @!flash-outline?
                             "inset 0 0 0 2px #34C759"
                             "inset 0 0 0 2px rgba(52, 199, 89, 0)")
               :transition "box-shadow 900ms ease-out"}}]
     [:div.flex.flex-col.gap-2.px-2.py-1.5.border-b.border-slate-200
      {:on-double-click (fn [^js event]
                          (when-not (header-interactive-target? event)
                            (on-toggle-collapsed transport-id)))}
      (if @!compact-layout?
        [:div.flex.flex-col.gap-1.flex-1.min-w-0
         [:div.flex.items-center.gap-1.5.flex-wrap.min-w-0 {:class "text-[12px]"}
          collapse-button
          (when draft?
            [:span.inline-flex.rounded.bg-amber-100.text-amber-800.px-1.py-0.font-bold {:class "text-[11px]"}
             (tr "Draft")])
          (when (and (not draft?) status)
            [transport-status-badge status])
          title-node]
         (when (or (seq carrier-label) (seq truck-or-carrier-code) (seq license-plate))
           [:div.flex.items-center.gap-1.5.flex-wrap.pl-5.min-w-0 {:class "text-[12px]"}
            [inline-separated
             [(when (seq carrier-label)
                [:span.text-slate-600 carrier-label])
              (when (seq truck-or-carrier-code)
                [:span.text-slate-600 (str (tr "Truck") ": " truck-or-carrier-code)])
              (when (seq license-plate)
                [:span.text-slate-600 license-plate])]]])
         [:div.flex.items-center.gap-1.5.flex-wrap.pl-5 {:class "text-[12px]"}
          (when (seq load-weight-label)
            [:span.text-slate-600.tabular-nums
             {:class "text-[11px]"
              :data-testid "transport-load-weight"}
             load-weight-label])
          [:span.inline-flex.rounded.px-1.py-0.font-bold
           {:class (str "text-[11px] "
                        (cond
                          (> (count vehicle-ids) 7) "bg-red-100 text-red-700"
                          (= (count vehicle-ids) 7) "bg-green-100 text-green-700"
                          :else "bg-slate-100 text-slate-700"))}
           (str (count vehicle-ids) "/7")]
          (when-not draft?
            [header-link-button
             {:on-click on-edit
              :testid "transport-edit-details"
              :label (tr "Edit")}])
          more-dropdown
          (when draft?
            [header-link-button
             {:on-click on-save-draft
              :testid "draft-save"
              :label (tr "Save")}])
          (when draft?
            [header-link-button
             {:on-click on-dismiss-draft
              :testid "draft-dismiss"
              :label (tr "Discard")}])]
         (when (seq waypoints-text)
           [:div.flex.items-center.gap-2.flex-wrap.pl-5
            [:div {:class "text-[12px] text-slate-500 whitespace-normal break-words"} waypoints-text]
            route-edit-button])]
        [:div.flex.flex-col.gap-0.5.flex-1.min-w-0
         [:div.flex.items-center.justify-between.gap-2
          [:div.flex.items-center.gap-1.5.flex-wrap.flex-1.min-w-0 {:class "text-[12px]"}
           collapse-button
           (when draft?
             [:span.inline-flex.rounded.bg-amber-100.text-amber-800.px-1.py-0.font-bold {:class "text-[11px]"}
              (tr "Draft")])
           (when (and (not draft?) status)
             [transport-status-badge status])
           title-node
           [inline-separated
            [(when (seq carrier-label)
               [:span.text-slate-600 carrier-label])
             (when (seq truck-or-carrier-code)
               [:span.text-slate-600 (str (tr "Truck") ": " truck-or-carrier-code)])
             (when (seq license-plate)
               [:span.text-slate-600 license-plate])]]]
          [:div.flex.items-center.gap-1.5.flex-wrap.justify-end
           (when (seq load-weight-label)
             [:span.text-slate-600.tabular-nums
              {:class "text-[11px]"
               :data-testid "transport-load-weight"}
              load-weight-label])
           [:span.inline-flex.rounded.px-1.py-0.font-bold
            {:class (str "text-[11px] "
                         (cond
                           (> (count vehicle-ids) 7) "bg-red-100 text-red-700"
                           (= (count vehicle-ids) 7) "bg-green-100 text-green-700"
                           :else "bg-slate-100 text-slate-700"))}
            (str (count vehicle-ids) "/7")]
           (when-not draft?
             [header-link-button
              {:on-click on-edit
               :testid "transport-edit-details"
               :label (tr "Edit")}])
           more-dropdown
           (when draft?
             [header-link-button
              {:on-click on-save-draft
               :testid "draft-save"
               :label (tr "Save")}])
           (when draft?
             [header-link-button
              {:on-click on-dismiss-draft
               :testid "draft-dismiss"
               :label (tr "Discard")}])]]
         (when (seq waypoints-text)
           [:div.flex.items-center.gap-2.pl-5
            [:div {:class "text-[12px] text-slate-500 whitespace-normal break-words"} waypoints-text]
            route-edit-button])])]
     [:<>
      (when-not collapsed?
        [:div
         (if (seq vehicle-ids)
           [:div.overflow-auto
            [table/table table-opts vehicles]]
           [:div.px-3.py-2.text-slate-500 {:class "text-[12px]"} (tr "No vehicles assigned")])])
      [waypoint-order-modal
       {:open? @!editing-waypoints?
        :transport-id transport-id
        :modal-load-title modal-load-title
        :modal-carrier-title modal-carrier-title
        :draft-waypoints @!draft-waypoints
        :draft-optimized-route? @!draft-optimized-route?
        :optimizing? @!optimizing-waypoints?
        :error @!waypoint-error
        :waypoint-key-fn waypoint-key-fn
        :on-close close-waypoint-editor!
        :on-save save-waypoint-editor!
        :on-set-draft-waypoints #(reset! !draft-waypoints (vec %))
        :on-set-optimized-route #(reset! !draft-optimized-route? %)
        :on-optimize-waypoints optimize-draft-waypoints!}]]]))

(defn vehicles-panel [{:keys [fahrzeuge assigned-ids staging-ids
                              selected-vehicle-ids on-select-row on-drag-select on-drag-select-preview on-open-assign-menu
                              on-row-double-click
                              col-settings show-cols-modal? on-close-cols-modal on-open-cols-modal on-change-col-settings
                              active-filters field-specs
                              on-remove-filter on-open-filters-modal on-open-filter-for-field
                              table-opts-fn filters-key available-cols column-order]}]
  (let [available (filterv (fn [v]
                             (and (not (contains? assigned-ids (:ductile/id v)))
                                  (not (contains? staging-ids (:ductile/id v)))))
                           fahrzeuge)
        !highlighted-filter-field (hooks/use-state nil)
        !vehicles-sorting (hooks/use-state nil)
        !header-menu (hooks/use-state nil)
        field-spec-map (filters/normalize-field-specs field-specs)
        selected-col-settings (columns/from-localstorage (or col-settings column-order))
        selected-profile-id (::columns/selected-profile-id selected-col-settings :default-profile)
        selected-cols (vec (or (get-in selected-col-settings [::columns/profiles selected-profile-id :cols]) []))
        pinned-col-attrs (into #{}
                               (keep (fn [{:keys [attr sticky?]}]
                                       (when sticky? attr)))
                               selected-cols)
        update-selected-cols! (fn [f]
                                (let [col-settings' (columns/from-localstorage (or col-settings column-order))
                                      selected-profile-id (::columns/selected-profile-id col-settings' :default-profile)
                                      cols-path [::columns/profiles selected-profile-id :cols]
                                      cols (vec (or (get-in col-settings' cols-path) []))]
                                  (on-change-col-settings
                                   (assoc-in col-settings' cols-path (vec (f cols))))))
        pin-col! (fn [col-path]
                   (update-selected-cols!
                    (fn [cols]
                      (mapv (fn [col]
                              (if (= (:attr col) col-path)
                                (assoc col :sticky? true)
                                col))
                            cols))))
        unpin-col! (fn [col-path]
                     (update-selected-cols!
                      (fn [cols]
                        (mapv (fn [col]
                                (if (= (:attr col) col-path)
                                  (assoc col :sticky? false)
                                  col))
                              cols))))
        hide-col! (fn [col-path]
                    (update-selected-cols!
                     (fn [cols]
                       (remove #(= (:attr %) col-path) cols))))
        visible (filters/apply-filters available field-specs active-filters)
        active-filter-fields (into #{}
                                   (map (comp filters/normalize-field-key ::filters/field))
                                   active-filters)
        table-opts (table-opts-fn {:col-settings col-settings
                                   :!sorting !vehicles-sorting
                                   :selected-rows (vec selected-vehicle-ids)
                                   :drag-select? true
                                   :on-drag-select-preview on-drag-select-preview
                                   :on-drag-select-commit on-drag-select
                                   :on-drag-select on-drag-select
                                   :header-on-context-menu
                                   (fn [col-path event]
                                     (let [field-key (filters/normalize-field-key col-path)]
                                       (reset! !header-menu
                                               {:x (.-clientX event)
                                                :y (.-clientY event)
                                                :col-path col-path
                                                :column-label (columns/label field-key)
                                                :pinned? (contains? pinned-col-attrs col-path)
                                                :filter-field (or (when (contains? field-spec-map col-path) col-path)
                                                                  (when (contains? field-spec-map field-key) field-key))})))
                                   :header-indicator-fn (fn [col-key]
                                                          (let [field (filters/normalize-field-key col-key)]
                                                            (when (contains? active-filter-fields field)
                                                              {:title (tr "Edit filters")
                                                               :data-testid (str "filtered-column-indicator-" (hash field))
                                                               :on-click (fn [_]
                                                                           (on-open-filters-modal))
                                                               :on-mouse-enter (fn [_]
                                                                                 (reset! !highlighted-filter-field field))
                                                               :on-mouse-leave (fn [_]
                                                                                 (reset! !highlighted-filter-field nil))})))
                                   :row-action (fn [vehicle event]
                                                 (on-select-row event (:ductile/id vehicle) (mapv :ductile/id visible)))
                                   :row-double-action (fn [vehicle event]
                                                        (on-row-double-click event (:ductile/id vehicle)))
                                   :row-on-context-menu (fn [vehicle event]
                                                          (.preventDefault event)
                                                          (on-open-assign-menu event (:ductile/id vehicle)))
                                   :virtualize-rows? true
                                   :virtual-scroll-container-selector ".load-builder-v2-vehicles-table"})]
    [:div.flex.flex-col.h-full
     [:div.px-2.py-1.border-b.border-slate-200
      [filters/filters-chip-bar {:filters active-filters
                                 :on-remove on-remove-filter
                                 :on-reset-all #(deframe/route-assoc! filters-key [])
                                 :on-open-modal on-open-filters-modal
                                 :highlight-field @!highlighted-filter-field
                                 :empty-label (tr "No active filters")}]]
     [:div.flex-auto.overflow-auto.load-builder-v2-vehicles-table
      [table/table table-opts visible]]
     (when show-cols-modal?
       [columns/select-columns-modal
        {:col-settings col-settings
         :available-cols available-cols
         :default-selected-cols column-order
         :on-close on-close-cols-modal
         :on-change (fn [new-col-settings]
                      (on-change-col-settings new-col-settings)
                      (on-close-cols-modal))}])
     (when-let [{:keys [x y col-path column-label pinned? filter-field]} @!header-menu]
       [popover/anchored-popover
        {:x x
         :y y
         :on-close #(reset! !header-menu nil)
         :data-testid "vehicles-header-menu"
         :est-width 260
         :est-height 260
         :class "rounded-lg border border-slate-200 bg-white shadow-[0_12px_28px_rgba(15,23,42,0.18)] overflow-hidden text-[12px]"
         :style {:min-width 240
                 :max-width 300}}
        [:<>
         [:button.w-full.text-left.px-3.py-1.hover:bg-slate-50.focus:bg-slate-50.outline-none
          {:data-popover-item "true"
           :role "menuitem"
           :data-testid "vehicles-header-menu-pin"
           :on-click #(do (if pinned?
                            (unpin-col! col-path)
                            (pin-col! col-path))
                          (reset! !header-menu nil))}
          (tr (if pinned? "Unpin %1" "Pin %1") column-label)]
         [:button.w-full.text-left.px-3.py-1.hover:bg-slate-50.focus:bg-slate-50.outline-none
          {:data-popover-item "true"
           :role "menuitem"
           :data-testid "vehicles-header-menu-hide"
           :on-click #(do (hide-col! col-path)
                          (reset! !header-menu nil))}
          (tr "Hide %1" column-label)]
         [:button.w-full.text-left.px-3.py-1.hover:bg-slate-50.focus:bg-slate-50.outline-none
          {:data-popover-item "true"
           :role "menuitem"
           :data-testid "vehicles-header-menu-configure"
           :on-click #(do (on-open-cols-modal)
                          (reset! !header-menu nil))}
          (str (tr "Configure columns") "…")]
         [:div.border-t.border-slate-200]
         [:button.w-full.text-left.px-3.py-1.hover:bg-slate-50.focus:bg-slate-50.outline-none
          {:data-popover-item "true"
           :role "menuitem"
           :data-testid "vehicles-header-menu-sort-asc"
           :on-click #(do (reset! !vehicles-sorting [col-path :asc])
                          (reset! !header-menu nil))}
          (tr "Sort ascending")]
         [:button.w-full.text-left.px-3.py-1.hover:bg-slate-50.focus:bg-slate-50.outline-none
          {:data-popover-item "true"
           :role "menuitem"
           :data-testid "vehicles-header-menu-sort-desc"
           :on-click #(do (reset! !vehicles-sorting [col-path :desc])
                          (reset! !header-menu nil))}
          (tr "Sort descending")]
         [:div.border-t.border-slate-200]
         [:button.w-full.text-left.px-3.py-1.hover:bg-slate-50.focus:bg-slate-50.outline-none
          {:data-popover-item "true"
           :role "menuitem"
           :data-testid "vehicles-header-menu-filter"
           :disabled (nil? filter-field)
           :class (when (nil? filter-field) "opacity-40 cursor-not-allowed")
           :on-click #(when filter-field
                        (on-open-filter-for-field filter-field)
                        (reset! !header-menu nil))}
          (str (tr "Filter by %1" column-label) "…")]]])]))

(defn staging-panel [{:keys [staging-ids vehicle-id->transport on-open-assign-menu
                             col-settings selected-vehicle-ids on-select-row on-drag-select on-drag-select-preview
                             table-opts-fn]}]
  (let [vehicles (vec (keep vehicle-id->transport staging-ids))
        table-opts (table-opts-fn
                    {:col-settings col-settings
                     :selected-rows (vec selected-vehicle-ids)
                     :drag-select? true
                     :on-drag-select-preview on-drag-select-preview
                     :on-drag-select-commit on-drag-select
                     :on-drag-select on-drag-select
                     :row-action (fn [vehicle event]
                                   (on-select-row event (:ductile/id vehicle) (mapv :ductile/id vehicles)))
                     :row-on-context-menu (fn [vehicle event]
                                            (.preventDefault event)
                                            (on-open-assign-menu event (:ductile/id vehicle)))})]
    [:div.flex.flex-col.h-full
     [:div.flex-auto.overflow-auto
      (if (seq staging-ids)
        [table/table table-opts vehicles]
        [:div.p-2.text-slate-500 {:class "text-[12px]"} (tr "No staged vehicles")])]]))

(defn- filter-item-for-field [active-filters field]
  (first (filter #(= field (::filters/field %)) active-filters)))

(defn- blank-filter-value? [operator value]
  (case operator
    :range (and (str/blank? (str (or (:from value) "")))
                (str/blank? (str (or (:to value) ""))))
    :boolean false
    (str/blank? (str (or value "")))))

(defn- upsert-inline-filter [active-filters field-specs field operator value]
  (let [existing (filter-item-for-field active-filters field)
        remaining (vec (remove #(= field (::filters/field %)) active-filters))]
    (if (blank-filter-value? operator value)
      remaining
      (let [item (assoc (or existing {::filters/id (random-uuid)})
                        ::filters/field field
                        ::filters/operator operator
                        ::filters/value value
                        ::filters/label nil)]
        (into remaining (filters/finalize-filters field-specs [item]))))))

(defn- filter-chip [{:keys [label on-remove]}]
  [:div.inline-flex.items-center.gap-1.rounded-full.h-5.px-2
   {:class "bg-[#e6edfa] text-[11px]"}
   [:span.font-medium label]
   [:button.text-slate-500.hover:text-slate-800
    {:type "button"
     :on-click on-remove}
    [ui/icon {:size 12} :cross-2]]])

(defn transport-filter-panel [{:keys [active-filters filters-open? field-specs rows on-change-filters on-toggle-open]}]
  (let [field-spec-map (filters/normalize-field-specs field-specs)
        status-options (or (when-let [choices-fn (::filters/choices-fn (get field-spec-map :transport/status-label))]
                             (choices-fn rows))
                           [])
        name-filter (some-> (filter-item-for-field active-filters :transport/display-name) ::filters/value)
        status-filter (some-> (filter-item-for-field active-filters :transport/status-label) ::filters/value)
        carrier-filter (some-> (filter-item-for-field active-filters :transport/carrier-truck) ::filters/value)
        license-filter (some-> (filter-item-for-field active-filters :transport/license-plate) ::filters/value)
        waypoints-filter (some-> (filter-item-for-field active-filters :transport/waypoints-text) ::filters/value)
        draft-filter-item (filter-item-for-field active-filters :transport/draft?)
        vehicle-count-filter (some-> (filter-item-for-field active-filters :transport/vehicle-count) ::filters/value)
        draft-filter-value (cond
                             (nil? draft-filter-item) "all"
                             (true? (::filters/value draft-filter-item)) "draft"
                             :else "saved")]
    [:div.border-b.border-slate-200.bg-slate-50
     (when (or filters-open? (seq active-filters))
       [:div.px-2.py-1.flex.flex-wrap.items-center.gap-1
        {:data-testid "transports-filter-chips"}
        (if (seq active-filters)
          (into
           [:<>]
           (map-indexed
            (fn [idx {:keys [::filters/id ::filters/label]}]
              ^{:key (str "transport-filter-chip-" id "-" idx)}
              [filter-chip {:label label
                            :on-remove #(on-change-filters
                                         (vec (remove (fn [item]
                                                        (= id (::filters/id item)))
                                                      active-filters)))}])
            active-filters))
          [:span.text-slate-500 {:class "text-[11px]"} (tr "No active filters")])
        (when (seq active-filters)
          [:button.bg-transparent.border-0.p-0.m-0.ml-1.text-indigo-700.hover:text-indigo-800.underline.underline-offset-2
           {:class "text-[11px]"
            :type "button"
            :data-testid "transports-filters-reset"
            :on-click #(on-change-filters [])}
           (tr "Reset all")])])
     (when filters-open?
       [:div.p-2.grid.gap-2
        {:class "grid-cols-1 md:grid-cols-2 xl:grid-cols-4"
         :data-testid "transports-filters-panel"}
        [:label.flex.flex-col.gap-1
         [:span.text-slate-600 {:class "text-[11px]"} (tr "Load")]
         [:input.border.border-slate-300.rounded.px-2.py-1
          {:class "text-[12px]"
           :data-testid "transports-filter-load"
           :type "text"
           :value (or name-filter "")
           :on-change #(on-change-filters
                        (upsert-inline-filter active-filters field-specs :transport/display-name :contains (.. % -target -value)))}]]
        [:label.flex.flex-col.gap-1
         [:span.text-slate-600 {:class "text-[11px]"} (tr "Status")]
         [:select.border.border-slate-300.rounded.px-2.py-1.bg-white
          {:class "text-[12px]"
           :data-testid "transports-filter-status"
           :value (or status-filter "")
           :on-change #(on-change-filters
                        (upsert-inline-filter active-filters field-specs :transport/status-label :exact (.. % -target -value)))}
          [:option {:value ""} (tr "All")]
          (for [status status-options]
            ^{:key (str "transport-status-option-" status)}
            [:option {:value status} status])]]
        [:label.flex.flex-col.gap-1
         [:span.text-slate-600 {:class "text-[11px]"} (tr "Carrier / Truck")]
         [:input.border.border-slate-300.rounded.px-2.py-1
          {:class "text-[12px]"
           :data-testid "transports-filter-carrier-truck"
           :type "text"
           :value (or carrier-filter "")
           :on-change #(on-change-filters
                        (upsert-inline-filter active-filters field-specs :transport/carrier-truck :contains (.. % -target -value)))}]]
        [:label.flex.flex-col.gap-1
         [:span.text-slate-600 {:class "text-[11px]"} (tr "License Plate")]
         [:input.border.border-slate-300.rounded.px-2.py-1
          {:class "text-[12px]"
           :data-testid "transports-filter-license-plate"
           :type "text"
           :value (or license-filter "")
           :on-change #(on-change-filters
                        (upsert-inline-filter active-filters field-specs :transport/license-plate :contains (.. % -target -value)))}]]
        [:label.flex.flex-col.gap-1
         [:span.text-slate-600 {:class "text-[11px]"} (tr "Waypoints")]
         [:input.border.border-slate-300.rounded.px-2.py-1
          {:class "text-[12px]"
           :data-testid "transports-filter-waypoints"
           :type "text"
           :value (or waypoints-filter "")
           :on-change #(on-change-filters
                        (upsert-inline-filter active-filters field-specs :transport/waypoints-text :contains (.. % -target -value)))}]]
        [:label.flex.flex-col.gap-1
         [:span.text-slate-600 {:class "text-[11px]"} (tr "Draft")]
         [:select.border.border-slate-300.rounded.px-2.py-1.bg-white
          {:class "text-[12px]"
           :data-testid "transports-filter-draft"
           :value draft-filter-value
           :on-change #(let [value (.. % -target -value)
                             next-filters (case value
                                            "draft" (upsert-inline-filter active-filters field-specs :transport/draft? :boolean true)
                                            "saved" (upsert-inline-filter active-filters field-specs :transport/draft? :boolean false)
                                            (vec (remove (fn [item]
                                                           (= :transport/draft? (::filters/field item)))
                                                         active-filters)))]
                         (on-change-filters next-filters))}
          [:option {:value "all"} (tr "All")]
          [:option {:value "draft"} (tr "Yes")]
          [:option {:value "saved"} (tr "No")]]]
        [:div.flex.flex-col.gap-1
         [:span.text-slate-600 {:class "text-[11px]"} (tr "Vehicle count")]
         [:div.flex.items-center.gap-1
          [:input.border.border-slate-300.rounded.px-2.py-1.w-full
           {:class "text-[12px]"
            :data-testid "transports-filter-vehicle-count-from"
            :type "text"
            :placeholder (tr "From")
            :value (or (:from vehicle-count-filter) "")
            :on-change #(on-change-filters
                         (upsert-inline-filter active-filters
                                               field-specs
                                               :transport/vehicle-count
                                               :range
                                               {:from (.. % -target -value)
                                                :to (or (:to vehicle-count-filter) "")}))}]
          [:span.text-slate-500 {:class "text-[11px]"} "–"]
          [:input.border.border-slate-300.rounded.px-2.py-1.w-full
           {:class "text-[12px]"
            :data-testid "transports-filter-vehicle-count-to"
            :type "text"
            :placeholder (tr "To")
            :value (or (:to vehicle-count-filter) "")
            :on-change #(on-change-filters
                         (upsert-inline-filter active-filters
                                               field-specs
                                               :transport/vehicle-count
                                               :range
                                               {:from (or (:from vehicle-count-filter) "")
                                                :to (.. % -target -value)}))}]]]])]))

(defn transports-panel [{:keys [loads draft-transports draft-order transport->vehicle-ids transport->waypoint-settings
                                active-filters filters-open? filter-field-specs filter-rows visible-transport-ids
                                vehicle-id->transport focused-transport-id
                                selected-vehicle-ids on-select-row on-drag-select on-drag-select-preview
                                on-focus-map on-hide-map on-edit on-save-draft on-dismiss-draft on-rename-draft on-right-click-vehicle
                                on-open-transport-menu
                                on-change-filters on-toggle-filters-open
                                on-save-waypoint-settings optimize-waypoints! waypoint-key-fn
                                transport-update-tokens
                                load-weight-label-fn
                                col-settings table-opts-fn]}]
  (let [persisted (->> loads
                       (sort-by (juxt (comp boolean :ductile/created-at)
                                      :ductile/created-at)
                                #(compare %2 %1))
                       (map (fn [load]
                              {:transport-id (lb2sel/load-key load)
                               :transport load
                               :draft? false})))
        persisted-ids (mapv :transport-id persisted)
        !collapsed-ids (hooks/use-state (into #{} persisted-ids))
        ordered-draft-ids (let [present? (set (keys draft-transports))
                                ranked (filter present? (reverse draft-order))
                                missing (remove (set ranked) (keys draft-transports))]
                            (concat ranked missing))
        drafts (map-indexed (fn [idx transport-id]
                              {:transport-id transport-id
                               :transport (get draft-transports transport-id)
                               :draft? true
                               :draft-index (inc idx)})
                            ordered-draft-ids)
        transports (concat drafts persisted)]
    (hooks/use-effect
     (fn []
       (let [persisted-id-set (set persisted-ids)
             draft-id-set (set (keys draft-transports))]
         (swap! !collapsed-ids
                (fn [ids]
                  (let [ids' (set ids)
                        existing (set (filter #(or (contains? persisted-id-set %)
                                                   (contains? draft-id-set %))
                                              ids'))]
                    (into existing persisted-id-set))))))
     [loads draft-transports])
    [:div.flex.flex-col.h-full
     [transport-filter-panel {:active-filters active-filters
                              :filters-open? filters-open?
                              :field-specs filter-field-specs
                              :rows filter-rows
                              :on-change-filters on-change-filters
                              :on-toggle-open on-toggle-filters-open}]
     [:div.flex-auto.overflow-auto.p-2.space-y-2.bg-slate-50
      (into
       [:<>]
       (for [{:keys [transport-id transport draft? draft-index]} transports
             :when (contains? visible-transport-ids transport-id)
             :let [vehicle-ids (get transport->vehicle-ids transport-id [])
                   persisted-waypoint-order (lb2sel/transport-persisted-waypoint-order-from-transport transport)
                   waypoints (lb2sel/transport-waypoints transport-id
                                                         transport->vehicle-ids
                                                         vehicle-id->transport
                                                         transport->waypoint-settings
                                                         persisted-waypoint-order)
                   waypoint-settings (get transport->waypoint-settings transport-id)
                   selected-in-transport (vec (filter (set vehicle-ids) selected-vehicle-ids))
                   weight-vehicle-ids (if (seq selected-in-transport)
                                        selected-in-transport
                                        vehicle-ids)
                   load-weight-label (when load-weight-label-fn
                                       (load-weight-label-fn weight-vehicle-ids))
                   transporter (:transport/transporter transport)
                   carrier (or (:transport/carrier transport)
                               (:transport/carrier transporter))
                   carrier-label (or (:address/name carrier)
                                     (:address/name (:transport/carrier transporter))
                                     (:truck/number transporter))
                   truck-or-carrier-code (or (:truck/number transporter)
                                             (:carrier/code carrier))
                   license-plate (or (:transport/license-plate transport)
                                     (:truck/license-plate transporter))
                   collapsed? (contains? @!collapsed-ids transport-id)]]
         ^{:key (str "transport-card-" transport-id)}
         [:div
          [transport-card {:transport-id transport-id
                           :transport transport
                           :vehicle-id->transport vehicle-id->transport
                           :carrier-label carrier-label
                           :status (when-not draft? (:transport/status transport))
                           :truck-or-carrier-code (when-not draft? truck-or-carrier-code)
                           :license-plate (when-not draft? license-plate)
                           :draft? draft?
                           :draft-index draft-index
                           :collapsed? collapsed?
                           :on-toggle-collapsed (fn [id]
                                                  (swap! !collapsed-ids
                                                         (fn [ids]
                                                           (if (contains? ids id)
                                                             (disj ids id)
                                                             (conj ids id)))))
                           :waypoints waypoints
                           :waypoint-settings waypoint-settings
                           :load-weight-label load-weight-label
                           :vehicle-ids vehicle-ids
                           :selected-vehicle-ids selected-vehicle-ids
                           :on-select-row on-select-row
                           :on-drag-select-preview on-drag-select-preview
                           :on-drag-select on-drag-select
                           :col-settings col-settings
                           :table-opts-fn table-opts-fn
                           :focused? (= focused-transport-id transport-id)
                           :on-focus-map #(on-focus-map transport-id)
                           :on-hide-map on-hide-map
                           :on-print-load-list (when (and (not draft?)
                                                          (not= :mode/ship (:transport/mode transport)))
                                                 #(js/window.open (transport/transport-link 'load-list transport)
                                                                  "_blank"))
                           :on-edit #(on-edit transport-id)
                           :on-save-draft #(on-save-draft transport-id)
                           :on-save-waypoint-settings on-save-waypoint-settings
                           :optimize-waypoints! optimize-waypoints!
                           :waypoint-key-fn waypoint-key-fn
                           :on-dismiss-draft #(on-dismiss-draft transport-id)
                           :on-open-transport-menu on-open-transport-menu
                           :on-rename-draft on-rename-draft
                           :on-right-click-vehicle on-right-click-vehicle
                           :update-token (get transport-update-tokens transport-id)}]]))]]))

(defn map-panel [{:keys [fahrzeuge focused-route fit-route-key route-loading? route-loading-label on-context-menu on-viewport-change]}]
  [:div.h-full.flex.flex-col.overflow-hidden.relative
   {:on-context-menu (fn [event]
                       (when on-context-menu
                         (.preventDefault event)
                         (.stopPropagation event)
                         (on-context-menu event)))}
   [:div.flex-auto.overflow-hidden
    [overview-map/view {:standalone? false
                        :rounded? false
                        :shadow? false
                        :border? false
                        :show-popup-button? false
                        :fit-route-key fit-route-key
                        :data fahrzeuge
                        :theme transport/theme
                        :show-flows? false
                        :route focused-route
                        :on-load-fn (fn [the-map]
                                      (when on-viewport-change
                                        (let [notify (fn [_] (on-viewport-change))]
                                          (.on the-map "moveend" notify)
                                          (notify nil))))}]]
   (when route-loading?
     [:div.absolute.inset-0.z-20.flex.items-center.justify-center.pointer-events-none
      {:data-testid "map-route-loading"}
      [:div.absolute.inset-0 {:class "bg-white/45"}]
      [:div.relative.rounded.border.border-slate-300.shadow-sm.px-3.py-2.flex.items-center.gap-2
       {:class "bg-white/95"}
       [ui/spinner {:size 14}]
       [:span {:class "text-[12px] text-slate-700"}
        (if (seq route-loading-label)
          (str (tr "Loading route") "... " route-loading-label)
          (str (tr "Loading route") "..."))]]])])

(defn render-load-builder-v2
  [{:keys [popup-mode?
           disconnected?
           popout-panel
           panel-meta
           panel->view
           col-settings
           on-change-col-settings
           on-open-cols-modal
           views
           selected-view-id
           view-modified?
           view-modified-lines
           on-select-view
           on-save-view
           on-rename-view
           on-duplicate-view
           on-delete-view
           on-save-view-changes
           on-save-view-as-new
           on-discard-view-changes
           show-fahrzeuge-on-transports?
           on-toggle-show-fahrzeuge-on-transports!
           layout-focus-panel
           popouts
           dispatch!
           panel-ids
           panel-titles
           popped-out-ids
           enforced-layout
           sid
           popup-url
           menu
           all-transport-ids
           draft-ids
           draft-order
           transport-summaries
           add-cell-filter!
           add-map-viewport-filter!
           on-copy-vins!
           on-open-vehicle-details!
           on-edit-transport-menu!
           on-save-draft-menu!
           on-dismiss-draft-menu!
           on-print-load-list-menu!
           on-focus-transport-map-menu!
           on-hide-transport-map-menu!
           on-request-remove-from-load!
           submit-shared-op!
           editing-transport-id
           show-edit-transport-modal?
           draft-id-set
           transport->vehicle-ids
           vehicle-id->transport
           available-actions
           on-finish-edit
           filters-modal-open?
           visible-vehicles-count
           available-vehicles-count
           field-specs
           all-available-vehicles
           draft-filters
           vehicles-filters-key
           vehicles-filters-draft-key
           vehicles-filters-modal-key
           on-filters-change
           on-filters-cancel
           on-filters-reset
           on-filters-apply
           vehicles-table-opts-fn
           last-update-ms
           auto-refresh-seconds]}]
  [:div
   {:class (if popup-mode?
             "h-[100dvh]"
             "-m-5 p-3 pb-0 ")
    :style {:height (if popup-mode?
                      "100dvh"
                      "calc(100dvh - 64px)")}}
   (when disconnected?
     [:div.fixed.inset-0.flex.items-center.justify-center
      {:class "bg-black/40 z-[120]"}
      [:div.rounded.bg-white.p-4.border.border-red-300.shadow
       [:div.font-bold.text-red-700 (tr "Lost connection to main window")]
       [:div.text-sm.mt-1 (tr "This popup is no longer connected.")]]])

   (if popup-mode?
     [:div.h-full
      [paneling/panel-card {:panel-id popout-panel
                            :title (get-in panel-meta [popout-panel :title] (tr "Panel"))
                            :header-right (get-in panel-meta [popout-panel :header-right])
                            :rounded? false
                            :child (panel->view popout-panel)}]]
     [:div.h-full.flex.flex-col.relative
      [:div.flex.items-center.gap-4.pb-2 {:class "text-[12px]"}
       [:div.flex.items-center.gap-3 {:class "text-[12px]"}
        [views-select-dropdown
         {:views views
          :selected-view-id selected-view-id
          :modified? view-modified?
          :modified-lines view-modified-lines
          :on-select on-select-view
          :on-save on-save-view
          :on-rename on-rename-view
          :on-duplicate on-duplicate-view
          :on-delete on-delete-view
          :on-save-changes on-save-view-changes
          :on-save-as-new on-save-view-as-new
          :on-discard-changes on-discard-view-changes
          :text-class "text-[12px]"}]
        [columns/select-profile-dropdown
         {:col-settings col-settings
          :text-class "text-[12px]"
          :on-change on-change-col-settings
          :on-modal-open on-open-cols-modal}]]
       [:div.flex.items-center.gap-1
        [:span.text-gray-600
         [ui/icon {:size 12} :panels]]
        (doall
         (let [focus-panels (paneling/selectable-focus-panels)]
           (for [[idx panel-id] (map-indexed vector focus-panels)]
             ^{:key (str "layout-focus-" (name panel-id))}
             [:<>
              (when (pos? idx)
                [:span.text-slate-400 "/"])
              [:button.bg-transparent.border-0.p-0.m-0.inline-flex.items-center.underline.underline-offset-2
               {:class (if (= panel-id layout-focus-panel)
                         "font-semibold text-slate-900 decoration-slate-400"
                         "text-slate-600 decoration-slate-300 hover:text-slate-900")
                :on-click (fn []
                            (let [popped-ids (keys popouts)]
                              (doseq [[_ w] popouts]
                                (when (and w (not (.-closed w)))
                                  (.close w)))
                              (dispatch! {:event :popouts/set :popouts {}})
                              (dispatch! {:event :layout/update
                                          :f (fn [layout]
                                               (-> layout
                                                   (update :hidden #(apply disj (or % #{}) popped-ids))
                                                   (paneling/reset-splits-for-focus panel-id)))})))}
               (or (get-in panel-titles [panel-id :title])
                   (get paneling/focus->label panel-id)
                   (name panel-id))]])))]
       [:div.flex.items-center.gap-3.ml-auto
        (doall
         (for [panel-id panel-ids
               :when (not= panel-id :vehicles)
               :let [hidden? (contains? (:hidden enforced-layout) panel-id)
                     popped? (contains? popped-out-ids panel-id)]]
           ^{:key (str "panel-toggle-inline-" (name panel-id))}
           [:button.bg-transparent.border-0.p-0.m-0.inline-flex.items-center.gap-1.underline.underline-offset-2
            {:class (cond
                      popped? "text-indigo-700 decoration-indigo-300 hover:text-indigo-800"
                      hidden? "text-slate-500 decoration-slate-300 hover:text-slate-700"
                      :else "text-slate-700 decoration-slate-300 hover:text-slate-900")
             :on-click #(do
                          (when-let [popup (get popouts panel-id)]
                            (when (and (not hidden?) popup (not (.-closed popup)))
                              (.close popup))
                            (when (and (not hidden?) popup)
                              (dispatch! {:event :popouts/update
                                          :f (fn [current-popouts]
                                               (dissoc current-popouts panel-id))})))
                          (dispatch! {:event :layout/update
                                      :f (fn [current-layout]
                                           (paneling/toggle-hidden current-layout panel-id))}))}
            [:span (get-in panel-titles [panel-id :title])]
            [:span.text-slate-500 (if hidden? (tr "Off") (tr "On"))]]))
        [last-update-indicator {:last-update-ms last-update-ms
                                :auto-refresh-seconds auto-refresh-seconds}]]]
      [:div.flex-auto.min-h-0
       [paneling/layout-grid
        {:layout enforced-layout
         :panel-meta panel-meta
         :panel->view panel->view
         :on-layout-change #(dispatch! {:event :layout/update
                                        :f (fn [current-layout]
                                             (% current-layout))})
         :exclude-panel-ids popped-out-ids
         :on-drop-panel (fn [src-panel-id dst-panel-id]
                          (when (and src-panel-id dst-panel-id)
                            (dispatch! {:event :layout/update
                                        :f (fn [current-layout]
                                             (let [layout' (paneling/swap-order current-layout src-panel-id dst-panel-id)
                                                   focus-id (:focus-panel layout')
                                                   layout'' (cond
                                                              (= focus-id src-panel-id) (assoc layout' :focus-panel dst-panel-id)
                                                              (= focus-id dst-panel-id) (assoc layout' :focus-panel src-panel-id)
                                                              :else layout')
                                                   secondary-id (or (:secondary-panel layout'') :staging)]
                                               (cond
                                                 (= secondary-id src-panel-id) (assoc layout'' :secondary-panel dst-panel-id)
                                                 (= secondary-id dst-panel-id) (assoc layout'' :secondary-panel src-panel-id)
                                                 :else layout'')))})))
         :on-popout (fn [panel-id]
                      (when (not= panel-id :vehicles)
                        (dispatch! {:event :layout/update
                                    :f #(assoc % :focus-panel nil)})
                        (if-let [existing (get popouts panel-id)]
                          (if (.-closed existing)
                            (dispatch! {:event :popouts/update
                                        :f #(dissoc % panel-id)})
                            (.focus existing))
                          (when-let [popup (js/window.open (popup-url panel-id sid)
                                                           "_blank"
                                                           "popup=true,width=1200,height=800")]
                            (dispatch! {:event :popouts/update
                                        :f #(assoc % panel-id popup)})))))}]]])

   (when-let [menu menu]
     [move-menu/view
      {:menu menu
       :context-filter-action (:context-filter-action menu)
       :transport-ids all-transport-ids
       :draft-ids draft-ids
       :draft-order draft-order
       :transport-summaries transport-summaries
       :show-extra-actions? true
       :show-fahrzeuge-on-transports? show-fahrzeuge-on-transports?
       :on-toggle-show-fahrzeuge-on-transports on-toggle-show-fahrzeuge-on-transports!
       :on-close #(dispatch! {:event :menu/clear})
       :on-add-filter (fn [action]
                        (if (= :map-viewport (:filter-item action))
                          (add-map-viewport-filter!)
                          (add-cell-filter! action)))
       :on-focus-selection-map (fn [vehicle-ids]
                                 (submit-shared-op! {:op :focus-selection-route
                                                     :vehicle-ids (vec vehicle-ids)}))
       :on-select-vehicles (fn [vehicle-ids]
                             (dispatch! {:event :selection/drag-commit
                                         :row-keys (vec vehicle-ids)
                                         :append? false}))
       :on-copy-vins (fn [vehicle-ids]
                       (when on-copy-vins!
                         (on-copy-vins! vehicle-ids)))
       :on-open-vehicle-details (fn [vehicle-id]
                                  (when on-open-vehicle-details!
                                    (on-open-vehicle-details! vehicle-id)))
       :on-edit-transport on-edit-transport-menu!
       :on-save-draft on-save-draft-menu!
       :on-dismiss-draft on-dismiss-draft-menu!
       :on-print-load-list on-print-load-list-menu!
       :on-focus-transport-map on-focus-transport-map-menu!
       :on-hide-transport-map on-hide-transport-map-menu!
       :on-move (fn [_source-id target-id vehicle-ids]
                  (submit-shared-op! {:op :move-to-transport
                                      :transport-id target-id
                                      :vehicle-ids (vec vehicle-ids)}))
       :on-move-to-staging (fn [_source-id vehicle-ids]
                             (submit-shared-op! {:op :move-to-staging
                                                 :vehicle-ids (vec vehicle-ids)}))
       :on-remove-from-load (fn [source-transport-id vehicle-ids]
                              (when on-request-remove-from-load!
                                (on-request-remove-from-load! source-transport-id
                                                              (vec vehicle-ids))))
       :on-remove-from-staging (fn [vehicle-ids]
                                 (submit-shared-op! {:op :remove-from-staging
                                                     :vehicle-ids (vec vehicle-ids)}))
       :on-create-with-vehicle (fn [_source-id vehicle-ids]
                                 (submit-shared-op! {:op :create-draft-with-vehicles
                                                     :vehicle-ids (vec vehicle-ids)}))}])

   (when (and show-edit-transport-modal? editing-transport-id)
     (let [transport-id editing-transport-id]
       [fields/edit-transport-dialog
        {:on-submit (fn [load]
                      (on-finish-edit transport-id load))
         :fixed-params {:transport/mode :mode/truck}
         :available-actions (or available-actions {})
         :show-debug-output? false
         :on-close #(do
                      (dispatch! {:event :ui/set-editing-transport
                                  :transport-id nil})
                      (deframe/route-dissoc! ::transport/edit-transport)
                      (deframe/route-dissoc! :current-load)
                      (deframe/tx! [[:op/dissoc-in [:current-route/transient :fetch :error]]]))}]))

   (when filters-modal-open?
     [filters/filters-modal
      {:title (str (tr "Vehicles filters") " · " visible-vehicles-count "/" available-vehicles-count)
       :field-specs field-specs
       :rows all-available-vehicles
       :filters draft-filters
       :preview-render (fn [{:keys [rows count all-count]}]
                         (let [preview-rows (vec (take 40 rows))
                               preview-table-opts (assoc (vehicles-table-opts-fn {:col-settings col-settings})
                                                         :virtualize-rows? false
                                                         :sticky-header? true)]
                           [:div.h-full.flex.flex-col
                            [:div.px-2.py-1.text-slate-600.border-b.border-slate-200
                             {:class "text-[12px]"}
                             (tr "Preview: %1/%2 vehicles" count all-count)]
                            [:div.flex-auto.overflow-auto.lbv2-filter-preview-scroll
                             [table/table preview-table-opts preview-rows]]]))
       :on-change on-filters-change
       :on-cancel on-filters-cancel
       :on-reset on-filters-reset
       :on-apply on-filters-apply}])])
